import { ChangeEvent, MouseEventHandler, useState } from "react";

export type SelectedEntity = {
  mrn: string;
};

export function useSelection<T extends SelectedEntity>() {
  const [selectedEntities, setSelectedEntities] = useState<Array<T>>([]);

  const isEntityChecked = (mrn: string) => {
    return Boolean(selectedEntities.find((entity) => entity.mrn === mrn));
  };

  const isGroupChecked = (entities: T[]) => {
    return entities.every((entity) => isEntityChecked(entity.mrn));
  };

  const isGroupIndeterminate = (entities: T[]) => {
    if (isGroupChecked(entities)) return false;

    return entities.some((entity) => isEntityChecked(entity.mrn));
  };

  const onGroupCheckChange = (
    _event: ChangeEvent<HTMLInputElement>,
    entities: T[],
  ) => {
    if (isGroupChecked(entities) || isGroupIndeterminate(entities)) {
      // uncheck all
      setSelectedEntities(
        selectedEntities.filter(
          (selectedEntity) =>
            !entities.find((entity) => selectedEntity.mrn === entity.mrn),
        ),
      );
    } else {
      // check all
      setSelectedEntities(entities);
    }
  };

  const handleCancelClick = () => {
    setSelectedEntities([]);
  };

  const handleNodeClick: MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  const handleNodeChange = (
    _: ChangeEvent<HTMLInputElement>,
    checked: boolean,
    entity: T,
  ) => {
    const nextSelected = selectedEntities.filter((s) => s.mrn !== entity.mrn);
    if (checked) {
      nextSelected.push(entity);
    }

    setSelectedEntities(nextSelected);
  };

  return {
    selectedEntities,
    setSelectedEntities,
    isEntityChecked,
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    handleCancelClick,
    handleNodeChange,
    handleNodeClick,
  };
}
